import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, CornerDownRight, LogOut, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, fieldErrors, unwrap } from '../../../api/client';
import { useAssetTypes } from '../../../api/queries';
import type { Asset, AssetDetail, AssetTreeNode, Part } from '../../../api/types';
import { AssetLink } from '../../../components/assets/AssetLink';
import { AssetPicker, LocationSelect } from '../../../components/pickers';
import {
  Button,
  Card,
  CardHeader,
  Checkbox,
  ConfirmDialog,
  DateInput,
  Dialog,
  EmptyState,
  ErrorBox,
  Field,
  FormGrid,
  Input,
  Loading,
  Notice,
  nullIfEmpty,
  Select,
  StatusBadge,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
  useToast,
} from '../../../components/ui';
import { formatDate, STATUS_LABELS, todayIso } from '../../../lib/format';
import { useAssetInvalidate } from '../LifecycleDialogs';

/** Flache Liste (recursive=true) → Baum in Anzeige-Reihenfolge. */
const orderTree = (rootId: string, nodes: AssetTreeNode[]): Array<AssetTreeNode & { level: number }> => {
  const byParent = new Map<string, AssetTreeNode[]>();
  for (const n of nodes) {
    const pid = n.parent?.id ?? rootId;
    byParent.set(pid, [...(byParent.get(pid) ?? []), n]);
  }
  const out: Array<AssetTreeNode & { level: number }> = [];
  const walk = (pid: string, level: number) => {
    for (const n of (byParent.get(pid) ?? []).sort((a, b) => a.asset_number.localeCompare(b.asset_number))) {
      out.push({ ...n, level });
      walk(n.id, level + 1);
    }
  };
  walk(rootId, 0);
  return out;
};

export function ComponentsTab({ asset, canWrite }: { asset: AssetDetail; canWrite: boolean }) {
  const [install, setInstall] = useState(false);
  const [removeChild, setRemoveChild] = useState<AssetTreeNode | null>(null);
  const [moveChild, setMoveChild] = useState<AssetTreeNode | null>(null);

  const children = useQuery({
    queryKey: ['asset', asset.id, 'children'],
    queryFn: () => unwrap(api.GET('/api/assets/{ref}/children', { params: { path: { ref: asset.id }, query: { recursive: 'true' } } })),
  });

  const tree = children.data ? orderTree(asset.id, children.data.data) : [];

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader
          title="Eingebaute Komponenten (mit Inventarnummer)"
          description="SSD, RAM, Netzteil … als eigene Assets – mit eigener Historie."
          actions={
            canWrite && (
              <Button size="sm" icon={<Plus className="size-4" />} onClick={() => setInstall(true)}>
                Komponente einbauen
              </Button>
            )
          }
        />
        {children.isPending ? (
          <Loading />
        ) : children.error ? (
          <ErrorBox error={children.error} className="m-4" />
        ) : tree.length === 0 ? (
          <EmptyState title="Keine Komponenten eingebaut" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Nr.</Th>
                <Th>Bezeichnung</Th>
                <Th>Typ</Th>
                <Th>Seriennummer</Th>
                <Th>Status</Th>
                {canWrite && <Th className="text-right">Aktion</Th>}
              </tr>
            </thead>
            <tbody>
              {tree.map((c) => (
                <Tr key={c.id}>
                  <Td className="whitespace-nowrap">
                    <span style={{ paddingLeft: c.level * 20 }} className="inline-flex items-center gap-1">
                      {c.level > 0 && <CornerDownRight className="size-3 text-slate-400" />}
                      <AssetLink asset={c} />
                    </span>
                  </Td>
                  <Td>{c.name}</Td>
                  <Td>{c.asset_type.label}</Td>
                  <Td className="font-mono text-xs">{c.serial_number ?? '–'}</Td>
                  <Td>
                    <StatusBadge status={c.status} />
                  </Td>
                  {canWrite && (
                    <Td className="text-right whitespace-nowrap">
                      {c.level === 0 && (
                        <>
                          <Button size="sm" variant="ghost" icon={<LogOut className="size-4" />} onClick={() => setRemoveChild(c)}>
                            Ausbauen
                          </Button>
                          <Button size="sm" variant="ghost" icon={<ArrowRightLeft className="size-4" />} onClick={() => setMoveChild(c)}>
                            Umbauen
                          </Button>
                        </>
                      )}
                    </Td>
                  )}
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <PartsCard asset={asset} canWrite={canWrite} />

      <InstallDialog parent={asset} open={install} onClose={() => setInstall(false)} />
      <RemoveChildDialog parent={asset} child={removeChild} onClose={() => setRemoveChild(null)} />
      <MoveChildDialog parent={asset} child={moveChild} onClose={() => setMoveChild(null)} />
    </div>
  );
}

function InstallDialog({ parent, open, onClose }: { parent: AssetDetail; open: boolean; onClose: () => void }) {
  const invalidate = useAssetInvalidate();
  const toast = useToast();
  const [child, setChild] = useState<Asset | null>(null);
  const [date, setDate] = useState(todayIso());
  const [note, setNote] = useState('');
  useEffect(() => {
    if (open) {
      setChild(null);
      setDate(todayIso());
      setNote('');
    }
  }, [open]);

  const m = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/assets/{ref}/children', {
          params: { path: { ref: parent.id } },
          body: { child: child!.id, move: !!child?.parent, effective_date: date || undefined, note: nullIfEmpty(note) },
        }),
      ),
    onSuccess: () => {
      invalidate();
      toast(`${child?.asset_number} eingebaut`);
      onClose();
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Komponente in ${parent.asset_number} einbauen`}
      onSubmit={() => m.mutate()}
      footer={
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant="primary" loading={m.isPending} disabled={!child}>
            {child?.parent ? 'Umbauen' : 'Einbauen'}
          </Button>
        </>
      }
    >
      <Field label="Komponente" required>
        {(p) => <AssetPicker id={p.id} value={child} onChange={setChild} exclude={[parent.id]} />}
      </Field>
      {child?.parent && (
        <Notice tone="warning">
          {child.asset_number} ist aktuell in {child.parent.asset_number} eingebaut und wird dort ausgebaut (Umbau).
        </Notice>
      )}
      <Field label="Datum">{(p) => <DateInput {...p} max={todayIso()} value={date} onChange={(e) => setDate(e.target.value)} />}</Field>
      <Field label="Notiz">{(p) => <Textarea {...p} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />}</Field>
      <ErrorBox error={m.error} />
    </Dialog>
  );
}

function RemoveChildDialog({ parent, child, onClose }: { parent: AssetDetail; child: AssetTreeNode | null; onClose: () => void }) {
  const invalidate = useAssetInvalidate();
  const toast = useToast();
  const [location, setLocation] = useState('');
  const [status, setStatus] = useState<'in_stock' | 'active' | 'repair' | 'retired' | 'lost' | 'unknown'>('in_stock');
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(todayIso());
  useEffect(() => {
    if (child) {
      setLocation('');
      setStatus('in_stock');
      setReason('');
      setDate(todayIso());
    }
  }, [child]);

  const m = useMutation({
    mutationFn: () =>
      unwrap(
        api.DELETE('/api/assets/{ref}/children/{childRef}', {
          params: { path: { ref: parent.id, childRef: child!.id } },
          body: { location: location || undefined, status, reason: nullIfEmpty(reason), effective_date: date || undefined },
        }),
      ),
    onSuccess: () => {
      invalidate();
      toast(`${child?.asset_number} ausgebaut`);
      onClose();
    },
  });

  return (
    <Dialog
      open={!!child}
      onClose={onClose}
      title={`${child?.asset_number ?? ''} ausbauen`}
      onSubmit={() => m.mutate()}
      footer={
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant="primary" loading={m.isPending}>
            Ausbauen
          </Button>
        </>
      }
    >
      <FormGrid>
        <Field label="Wohin?" hint="Leer = Standort des Geräts">
          {(p) => <LocationSelect {...p} value={location} onChange={setLocation} emptyLabel="– Standort des Geräts –" />}
        </Field>
        <Field label="Neuer Status">
          {(p) => (
            <Select {...p} value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
              {(['in_stock', 'active', 'repair', 'retired', 'lost', 'unknown'] as const).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </FormGrid>
      <Field label="Grund">{(p) => <Textarea {...p} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="z. B. Ersatzteil, defekt" />}</Field>
      <Field label="Datum">{(p) => <DateInput {...p} max={todayIso()} value={date} onChange={(e) => setDate(e.target.value)} />}</Field>
      <ErrorBox error={m.error} />
    </Dialog>
  );
}

function MoveChildDialog({ parent, child, onClose }: { parent: AssetDetail; child: AssetTreeNode | null; onClose: () => void }) {
  const invalidate = useAssetInvalidate();
  const toast = useToast();
  const [target, setTarget] = useState<Asset | null>(null);
  const [date, setDate] = useState(todayIso());
  const [note, setNote] = useState('');
  useEffect(() => {
    if (child) {
      setTarget(null);
      setDate(todayIso());
      setNote('');
    }
  }, [child]);

  const m = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/assets/{ref}/children', {
          params: { path: { ref: target!.id } },
          body: { child: child!.id, move: true, effective_date: date || undefined, note: nullIfEmpty(note) },
        }),
      ),
    onSuccess: () => {
      invalidate();
      toast(`${child?.asset_number} nach ${target?.asset_number} umgebaut`);
      onClose();
    },
  });

  return (
    <Dialog
      open={!!child}
      onClose={onClose}
      title={`${child?.asset_number ?? ''} umbauen`}
      onSubmit={() => m.mutate()}
      footer={
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant="primary" loading={m.isPending} disabled={!target}>
            Umbauen
          </Button>
        </>
      }
    >
      <p className="text-sm text-slate-600">
        Aus {parent.asset_number} ausbauen und direkt in ein anderes Gerät einbauen.
      </p>
      <Field label="Zielgerät" required>
        {(p) => <AssetPicker id={p.id} value={target} onChange={setTarget} exclude={[parent.id, child?.id ?? '']} />}
      </Field>
      <Field label="Datum">{(p) => <DateInput {...p} max={todayIso()} value={date} onChange={(e) => setDate(e.target.value)} />}</Field>
      <Field label="Notiz">{(p) => <Textarea {...p} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />}</Field>
      <ErrorBox error={m.error} />
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Komponenten ohne Inventarnummer (Parts)
// ---------------------------------------------------------------------------

function PartsCard({ asset, canWrite }: { asset: AssetDetail; canWrite: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [showRemoved, setShowRemoved] = useState(false);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<Part | null>(null);

  const parts = useQuery({
    queryKey: ['asset', asset.id, 'parts', showRemoved],
    queryFn: () => unwrap(api.GET('/api/assets/{ref}/parts', { params: { path: { ref: asset.id }, query: { include_removed: showRemoved ? 'true' : undefined } } })),
  });

  const remove = useMutation({
    mutationFn: (reason: string) =>
      unwrap(api.DELETE('/api/assets/{ref}/parts/{partId}', { params: { path: { ref: asset.id, partId: removing!.id } }, body: { reason: reason || null } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['asset', asset.id] });
      toast('Komponente ausgebaut');
      setRemoving(null);
    },
  });

  return (
    <Card>
      <CardHeader
        title="Komponenten ohne Inventarnummer"
        description="Z. B. 4× 32 GB RAM – nur als Liste am Gerät."
        actions={
          <>
            <Checkbox label="Ausgebaute zeigen" checked={showRemoved} onChange={(e) => setShowRemoved(e.target.checked)} />
            {canWrite && (
              <Button size="sm" icon={<Plus className="size-4" />} onClick={() => setAdding(true)}>
                Hinzufügen
              </Button>
            )}
          </>
        }
      />
      {parts.isPending ? (
        <Loading />
      ) : parts.error ? (
        <ErrorBox error={parts.error} className="m-4" />
      ) : parts.data.data.length === 0 ? (
        <EmptyState title="Keine Einträge" />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Bezeichnung</Th>
              <Th>Menge</Th>
              <Th>Hersteller / Modell</Th>
              <Th>Teile-/Seriennr.</Th>
              <Th>Eingebaut</Th>
              {canWrite && <Th />}
            </tr>
          </thead>
          <tbody>
            {parts.data.data.map((p) => (
              <Tr key={p.id} className={p.is_installed ? undefined : 'text-slate-400'}>
                <Td>
                  {p.name}
                  {p.asset_type && <span className="block text-xs text-slate-500">{p.asset_type.label}</span>}
                  {p.description && <span className="block text-xs text-slate-500">{p.description}</span>}
                </Td>
                <Td>{p.quantity}×</Td>
                <Td>{[p.manufacturer, p.model].filter(Boolean).join(' ') || '–'}</Td>
                <Td className="font-mono text-xs">{[p.part_number, p.serial_number].filter(Boolean).join(' / ') || '–'}</Td>
                <Td className="whitespace-nowrap">
                  {formatDate(p.installed_at)}
                  {!p.is_installed && <span className="block text-xs">ausgebaut {formatDate(p.removed_at)}</span>}
                </Td>
                {canWrite && (
                  <Td className="text-right">
                    {p.is_installed && (
                      <Button size="sm" variant="ghost" icon={<Trash2 className="size-4" />} onClick={() => setRemoving(p)}>
                        Ausbauen
                      </Button>
                    )}
                  </Td>
                )}
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
      <AddPartDialog asset={asset} open={adding} onClose={() => setAdding(false)} />
      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        title="Komponente ausbauen"
        message={`„${removing?.name}“ wird als ausgebaut markiert (bleibt in der Historie).`}
        requireReason
        reasonLabel="Grund"
        confirmLabel="Ausbauen"
        loading={remove.isPending}
        error={remove.error}
        onConfirm={(r) => remove.mutate(r)}
      />
    </Card>
  );
}

function AddPartDialog({ asset, open, onClose }: { asset: AssetDetail; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const types = useAssetTypes();
  const empty = { name: '', asset_type: '', manufacturer: '', model: '', part_number: '', serial_number: '', quantity: '1', description: '' };
  const [f, setF] = useState(empty);
  useEffect(() => {
    if (open) setF(empty);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const m = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/assets/{ref}/parts', {
          params: { path: { ref: asset.id } },
          body: {
            name: f.name.trim(),
            asset_type: f.asset_type || undefined,
            manufacturer: nullIfEmpty(f.manufacturer),
            model: nullIfEmpty(f.model),
            part_number: nullIfEmpty(f.part_number),
            serial_number: nullIfEmpty(f.serial_number),
            quantity: Math.max(1, Number(f.quantity) || 1),
            description: nullIfEmpty(f.description),
          },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['asset', asset.id] });
      toast('Komponente hinzugefügt');
      onClose();
    },
  });
  const errs = fieldErrors(m.error);
  const set = (k: keyof typeof empty) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Komponente ohne Inventarnummer"
      onSubmit={() => m.mutate()}
      footer={
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant="primary" loading={m.isPending} disabled={!f.name.trim()}>
            Hinzufügen
          </Button>
        </>
      }
    >
      <FormGrid>
        <Field label="Bezeichnung" required error={errs.name} className="sm:col-span-2">
          {(p) => <Input {...p} value={f.name} onChange={set('name')} placeholder="z. B. RAM 32 GB DDR5" />}
        </Field>
        <Field label="Typ">
          {(p) => (
            <Select {...p} value={f.asset_type} onChange={set('asset_type')}>
              <option value="">–</option>
              {types.data
                ?.filter((t) => t.category === 'component')
                .map((t) => (
                  <option key={t.id} value={t.key}>
                    {t.label}
                  </option>
                ))}
            </Select>
          )}
        </Field>
        <Field label="Menge" error={errs.quantity}>
          {(p) => <Input {...p} type="number" min={1} value={f.quantity} onChange={set('quantity')} />}
        </Field>
        <Field label="Hersteller">{(p) => <Input {...p} value={f.manufacturer} onChange={set('manufacturer')} />}</Field>
        <Field label="Modell">{(p) => <Input {...p} value={f.model} onChange={set('model')} />}</Field>
        <Field label="Teilenummer">{(p) => <Input {...p} value={f.part_number} onChange={set('part_number')} />}</Field>
        <Field label="Seriennummer">{(p) => <Input {...p} value={f.serial_number} onChange={set('serial_number')} />}</Field>
        <Field label="Beschreibung" className="sm:col-span-2">
          {(p) => <Textarea {...p} rows={2} value={f.description} onChange={set('description')} />}
        </Field>
      </FormGrid>
      <ErrorBox error={m.error} />
    </Dialog>
  );
}
